import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, uploadImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Pencil, Trash2, Plus, LogOut, Upload } from "lucide-react";

const CATEGORIES = ["Governance", "HR Governance", "People Risk", "Transformation", "Leadership", "Regulatory", "Case Study"];
const emptyArticle = { title: "", excerpt: "", content: "", category: "Governance", cover_image: "", published: true };

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("articles");
  const [articles, setArticles] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyArticle);
  const [saving, setSaving] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setCoverUploading(true);
    try {
      const url = await uploadImage(file);
      set("cover_image", url);
      toast.success("Cover image uploaded.");
    } catch (err) {
      toast.error("Cover upload failed.");
    } finally {
      setCoverUploading(false);
    }
  };

  const load = () => {
    api.get("/admin/articles").then((r) => setArticles(r.data)).catch(() => {});
    api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyArticle); setDialogOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ title: a.title, excerpt: a.excerpt, content: a.content, category: a.category, cover_image: a.cover_image || "", published: a.published });
    setDialogOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title || !form.excerpt || !form.content) { toast.error("Title, excerpt and content are required."); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/articles/${editing.id}`, form);
      else await api.post("/admin/articles", form);
      toast.success(editing ? "Article updated." : "Article published.");
      setDialogOpen(false); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    await api.delete(`/admin/articles/${a.id}`);
    toast.success("Article deleted."); load();
  };

  const field = "bg-elevated border border-white/15 rounded-sm text-bone focus-visible:ring-gold";

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#0A0A0A] p-6 md:p-10">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-10 border-b border-white/10 pb-6">
          <div>
            <h1 className="font-serif text-3xl">Advisor Console</h1>
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted2 mt-1">{user?.email}</p>
          </div>
          <button data-testid="admin-logout" onClick={() => { logout(); nav("/"); }}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-muted2 hover:text-gold transition-colors">
            <LogOut size={14} /> Logout
          </button>
        </div>

        <div className="flex gap-6 mb-8">
          {["articles", "enquiries"].map((t) => (
            <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)}
              className={`font-mono text-xs uppercase tracking-[0.2em] pb-2 border-b-2 transition-colors ${tab === t ? "text-gold border-gold" : "text-muted2 border-transparent"}`}>
              {t} {t === "articles" ? `(${articles.length})` : `(${contacts.length})`}
            </button>
          ))}
        </div>

        {tab === "articles" && (
          <div data-testid="admin-articles">
            <button data-testid="admin-new-article" onClick={openNew}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] bg-gold text-ink px-5 py-3 mb-6 hover:bg-goldhover transition-colors">
              <Plus size={14} /> New Article
            </button>
            <div className="border border-white/10">
              {articles.map((a) => (
                <div key={a.id} data-testid={`admin-article-row-${a.id}`} className="flex items-center gap-4 px-4 py-4 border-b border-white/10 last:border-0 hover:bg-elevated transition-colors">
                  <span className={`w-2 h-2 rounded-full ${a.published ? "bg-gold" : "bg-white/20"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-medium">{a.title}</p>
                    <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted2 mt-1">{a.category} · {a.published ? "Published" : "Draft"}</p>
                  </div>
                  <button data-testid={`admin-edit-${a.id}`} onClick={() => openEdit(a)} className="text-muted2 hover:text-gold transition-colors"><Pencil size={16} /></button>
                  <button data-testid={`admin-delete-${a.id}`} onClick={() => remove(a)} className="text-muted2 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
                </div>
              ))}
              {articles.length === 0 && <p className="p-6 text-muted2 font-mono text-sm">No articles yet.</p>}
            </div>
          </div>
        )}

        {tab === "enquiries" && (
          <div data-testid="admin-enquiries" className="border border-white/10">
            {contacts.map((c) => (
              <div key={c.id} data-testid={`admin-contact-row-${c.id}`} className="px-4 py-5 border-b border-white/10 last:border-0">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="font-medium">{c.name} <span className="text-muted2 font-normal">· {c.company || "—"}</span></p>
                  <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-gold">{c.area_of_interest}</span>
                </div>
                <p className="text-sm text-muted2 mt-2">{c.message}</p>
                <div className="flex gap-4 mt-3 font-mono text-[10px] tracking-[0.1em] uppercase text-muted2">
                  <span>{c.email}</span><span>{new Date(c.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
            {contacts.length === 0 && <p className="p-6 text-muted2 font-mono text-sm">No enquiries yet.</p>}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="admin-article-dialog" className="bg-surface border-white/15 text-bone max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl font-light">{editing ? "Edit Article" : "New Article"}</DialogTitle>
            <DialogDescription className="text-muted2 text-sm">Compose your insight with rich formatting and images. Toggle Published to control public visibility.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <Input data-testid="article-form-title" className={field} placeholder="Title" value={form.title} onChange={(e) => set("title", e.target.value)} />
            <Textarea data-testid="article-form-excerpt" className={field} placeholder="Excerpt (short summary)" value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} />
            <RichTextEditor value={form.content} onChange={(html) => set("content", html)} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="article-form-category" className={field}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-elevated border-white/15 text-bone">
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c} className="focus:bg-gold focus:text-ink">{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Input data-testid="article-form-image" className={field + " flex-1"} placeholder="Cover image URL" value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} />
                <label data-testid="article-form-image-upload" className="shrink-0 cursor-pointer border border-white/15 rounded-sm h-10 w-10 flex items-center justify-center text-muted2 hover:text-gold hover:border-gold transition-colors">
                  <Upload size={15} />
                  <input type="file" accept="image/*" className="hidden" onChange={uploadCover} />
                </label>
              </div>
            </div>
            {coverUploading && <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-gold">Uploading cover…</p>}
            {form.cover_image && <img src={form.cover_image} alt="cover preview" className="w-full max-h-40 object-cover rounded-sm border border-white/10" />}
            <div className="flex items-center gap-3">
              <Switch data-testid="article-form-published" checked={form.published} onCheckedChange={(v) => set("published", v)} />
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-muted2">{form.published ? "Published" : "Draft"}</span>
            </div>
          </div>
          <DialogFooter>
            <button data-testid="article-form-save" onClick={save} disabled={saving}
              className="font-sans text-xs uppercase tracking-[0.2em] bg-gold text-ink px-8 py-3 hover:bg-goldhover transition-colors disabled:opacity-50">
              {saving ? "Saving…" : editing ? "Update" : "Publish"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
